import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { HeartHandshake, Check, ChevronDown, Clock, Gift } from "lucide-react";

const GROUP_ORDER = ["Setup", "Registration", "Judging", "Operations", "Community", "Cleanup"];
const SHIRT_SIZES = ["Youth S", "Youth M", "Youth L", "Adult S", "Adult M", "Adult L", "Adult XL", "Adult 2XL"];
const GROUP_BLURB = {
  Setup: "Early birds — build the site before doors open (6–8 AM).",
  Registration: "Greet competitors, check waivers, hand out jerseys.",
  Judging: "Judges & scorekeepers running the hiheats.org scoring.",
  Operations: "Keep the day flowing — marshals, water safety, MC, runners.",
  Community: "Keiki zone, vendors, media, bucket garden & aina patrol.",
  Cleanup: "Leave Pounders better than we found it (2:30–4:30 PM).",
};
const TIMELINE = [
  ["6:00 AM", "Setup begins — tents, judges' area, PA, banners, keiki zone"],
  ["7:00 AM", "Check-in & registration opens; packet pickup"],
  ["7:45 AM", "Competitor meeting & pule / opening protocol"],
  ["8:00 AM", "First heat in the water"],
  ["10:00 AM", "Judge Panel 2 rotates in"],
  ["12:00 PM", "Judge Panel 3 rotates in; finals block begins"],
  ["2:00 PM", "Awards, mahalo & sponsor recognition, giveaways"],
  ["2:30 PM", "Breakdown & cleanup begins"],
  ["4:30 PM", "Site clear"],
];

const inputCls = "w-full brutal-2 p-3 font-display focus:outline-none bg-white";

export default function Volunteer() {
  const [shifts, setShifts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [f, setF] = useState({ name: "", email: "", phone: "", notes: "", shirt_size: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [wishlist, setWishlist] = useState("");

  const load = () => api.volunteerShifts().then(setShifts).catch(() => {});
  useEffect(() => { load(); api.config().then((c) => setWishlist(c.amazon_wishlist_url || "")).catch(() => {}); }, []);

  const toggle = (sid, remaining) => {
    setError("");
    if (selected.includes(sid)) setSelected(selected.filter((s) => s !== sid));
    else if (remaining > 0) setSelected([...selected, sid]);
  };

  const submit = async () => {
    setError("");
    if (!f.name.trim() || !f.email.includes("@")) { setError("Please enter your name and a valid email."); return; }
    if (!f.shirt_size) { setError("Please pick your T-shirt size so we can order your shirt."); return; }
    if (selected.length === 0) { setError("Pick at least one shift you'd like to help with."); return; }
    setBusy(true);
    try {
      const res = await api.volunteerRegister({ ...f, shift_ids: selected });
      setDone(res);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not sign up. Please try again.");
      load();
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto p-4 pt-10 pb-28" data-testid="volunteer-success">
        <div className="brutal p-6 text-center">
          <HeartHandshake size={56} className="mx-auto text-[#E63946] mb-3" />
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Mahalo, {done.name}!</h1>
          <p className="font-display mt-2 text-gray-700">Your volunteer request is in for Pounders Fest 2026.</p>
          <div className="brutal-2 mt-4 p-3 text-left">
            <div className="font-display text-xs uppercase tracking-widest text-gray-500 mb-1">Requested shifts</div>
            <ul>{done.shifts.map((s) => <li key={s} className="font-display font-bold">{s}</li>)}</ul>
          </div>
          <div className="brutal-2 bg-[#FFD700] p-3 mt-3 text-left">
            <p className="font-display text-sm font-bold">⏳ Pending confirmation</p>
            <p className="font-display text-sm mt-1">An organizer will review and confirm your shifts. You'll get an <strong>approval email</strong> once you're officially on the crew.</p>
          </div>
          <button data-testid="volunteer-another" onClick={() => { setDone(null); setSelected([]); setF({ name: "", email: "", phone: "", notes: "" }); load(); }} className="mt-5 bg-black text-white font-display font-black uppercase tracking-widest px-6 py-3">
            Sign Up Another Volunteer
          </button>
        </div>
      </div>
    );
  }

  const grouped = GROUP_ORDER.map((g) => ({ group: g, items: shifts.filter((s) => s.group === g) })).filter((x) => x.items.length);

  return (
    <div className="max-w-lg mx-auto p-4 pt-8 pb-28" data-testid="volunteer-view">
      <div className="flex items-center gap-2 mb-1">
        <HeartHandshake size={30} className="text-[#E63946]" />
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">Volunteer</h1>
      </div>
      <p className="font-display text-gray-600 text-sm mb-1">Farm Kine Tings · Pounders Beach · September 2026</p>
      <div className="brutal-2 bg-[#FFD700] p-3 mb-5">
        <p className="font-display text-sm font-bold">🤙 Guiding rule: no volunteer serves more than one 2-hour shift block at a time — so everyone gets to enjoy the day.</p>
      </div>

      {wishlist && (
        <a
          data-testid="volunteer-wishlist-link"
          href={wishlist}
          target="_blank"
          rel="noopener noreferrer"
          className="block brutal p-3 mb-5 bg-white active:scale-[0.99] transition-transform"
        >
          <div className="flex items-center gap-2">
            <Gift size={20} className="text-[#E63946] shrink-0" />
            <span className="font-display font-black uppercase text-sm">Can't make a shift? Gift us gear 🎁</span>
          </div>
          <p className="font-display text-xs text-gray-600 mt-1">Buy a needed item off our Amazon wishlist — ships straight to the event. Tap to shop.</p>
        </a>
      )}

      <button data-testid="toggle-timeline" onClick={() => setShowTimeline((s) => !s)} className="w-full brutal-2 p-3 flex items-center justify-between mb-5">
        <span className="font-display font-black uppercase text-sm flex items-center gap-2"><Clock size={16} /> Event Day Timeline</span>
        <ChevronDown size={18} className={`transition-transform ${showTimeline ? "rotate-180" : ""}`} />
      </button>
      {showTimeline && (
        <div className="brutal-2 p-3 mb-5 space-y-1" data-testid="timeline-content">
          {TIMELINE.map(([t, a]) => (
            <div key={t} className="flex gap-3 text-sm font-display">
              <span className="font-score w-20 shrink-0">{t}</span>
              <span className="text-gray-700">{a}</span>
            </div>
          ))}
        </div>
      )}

      {/* Contact */}
      <div className="brutal p-4 mb-5" data-testid="volunteer-contact">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-3">Your Info</h2>
        <div className="space-y-2">
          <input data-testid="vol-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className={inputCls} />
          <input data-testid="vol-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={inputCls} />
          <input data-testid="vol-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" className={inputCls} />
          <select data-testid="vol-shirt" value={f.shirt_size} onChange={(e) => setF({ ...f, shirt_size: e.target.value })} className={inputCls}>
            <option value="">T-shirt size…</option>
            {SHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <textarea data-testid="vol-notes" rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Anything we should know? (skills, group…)" className={inputCls} />
        </div>
      </div>

      {/* Shift picker */}
      <div className="mb-5" data-testid="volunteer-shifts">
        <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Pick Your Shifts</h2>
        <p className="font-display text-xs text-gray-500 mb-3">Tap the roles & times you'd like. Full slots are greyed out.</p>
        {grouped.map(({ group, items }) => {
          const cap = items.reduce((a, s) => a + s.capacity, 0);
          const filled = items.reduce((a, s) => a + s.filled, 0);
          const pct = cap > 0 ? Math.min(100, Math.round((filled / cap) * 100)) : 0;
          return (
          <div key={group} className="mb-4">
            <div className="font-display font-black uppercase text-sm bg-black text-white px-2 py-1 inline-block">{group}</div>
            <p className="font-display text-[11px] text-gray-500 mt-1">{GROUP_BLURB[group]}</p>
            <div className="flex items-center gap-2 mt-1 mb-2" data-testid={`group-progress-${group}`}>
              <div className="brutal-2 h-3 flex-1 bg-white overflow-hidden">
                <div className="h-full bg-[#E63946] transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-display text-[10px] uppercase tracking-widest text-gray-500 shrink-0">{filled} of {cap} filled</span>
            </div>
            <div className="space-y-2">
              {items.map((s) => {
                const isSel = selected.includes(s.id);
                const full = s.remaining <= 0;
                return (
                  <button
                    key={s.id}
                    data-testid={`shift-${s.id}`}
                    disabled={full && !isSel}
                    onClick={() => toggle(s.id, s.remaining)}
                    className={`w-full text-left brutal-2 p-3 transition-transform active:scale-[0.99] ${
                      isSel ? "bg-[#E63946] text-white" : full ? "bg-gray-100 opacity-70" : "bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-display font-bold uppercase text-sm truncate">{s.role}</div>
                        <div className={`font-display text-[11px] ${isSel ? "text-white/80" : "text-gray-500"}`}>{s.time}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`font-display text-[10px] uppercase tracking-widest ${isSel ? "text-white" : full ? "text-[#E63946] font-bold" : "text-gray-400"}`}>
                          {full ? "Full" : `${s.remaining} left`}
                        </span>
                        {isSel && <Check size={18} />}
                      </div>
                    </div>
                    {(s.crew.length > 0 || s.pending > 0) && (
                      <div className="flex flex-wrap gap-1 mt-2" data-testid={`crew-${s.id}`}>
                        {s.crew.map((n, i) => (
                          <span key={i} className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${isSel ? "bg-white text-[#E63946]" : "bg-green-600 text-white"}`}>{n}</span>
                        ))}
                        {s.pending > 0 && (
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${isSel ? "bg-white/30 text-white" : "bg-gray-200 text-gray-600"}`}>+{s.pending} pending</span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>

      {error && <p className="text-[#E63946] font-bold uppercase text-sm mb-3" data-testid="volunteer-error">{error}</p>}
      <button
        data-testid="volunteer-submit"
        onClick={submit}
        disabled={busy}
        className="w-full bg-black text-white font-display font-black uppercase tracking-widest py-4 active:scale-95 transition-transform disabled:opacity-60"
      >
        {busy ? "Signing up…" : `Sign Up${selected.length ? ` · ${selected.length} shift${selected.length > 1 ? "s" : ""}` : ""}`}
      </button>
    </div>
  );
}
